import {
  BulletComponent,
  EducationalScreenBodyChildResultV2,
  EducationalScreenHeaderChildResultV2,
  Scalars,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useGetEligibleNonEligibleDetails from 'features/lineOfCredit/screens/eligibility/hooks/useGetEligibleNonEligibleDetails';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface ExtendedBulletComponent extends BulletComponent {
  bulletItems: Array<Scalars['Copy']>;
}

const NotEligibleHeader = ({
  header,
}: {
  header: EducationalScreenHeaderChildResultV2[];
}) => {
  return header.map((item) => {
    if (item.__typename === Typenames.TextComponent) {
      return (
        <TitleLgText key={item.id} style={styles.title}>
          {item.copy}
        </TitleLgText>
      );
    }

    return null;
  });
};

const NotEligibleContent = ({
  body,
}: {
  body: EducationalScreenBodyChildResultV2[];
}) => {
  return body.map((item) => {
    if (item.__typename === Typenames.TextComponent) {
      return (
        <BodyBaseText key={item.id} style={styles.defaultStyle}>
          {item.copy}
        </BodyBaseText>
      );
    } else if (item.__typename === Typenames.BulletComponent) {
      return (
        <View key={item.id} style={styles.defaultStyle}>
          <ContentList
            contentList={(item as ExtendedBulletComponent).bulletItems}
          />
        </View>
      );
    }

    return null;
  });
};

const NotEligibleScreen = () => {
  const router = useRouter();
  const { vlocPageId } = useLocalSearchParams<{
    vlocPageId: string;
  }>();

  const { bottom } = useSafeAreaInsets();

  const { isFetching, error, serializedData } =
    useGetEligibleNonEligibleDetails(vlocPageId);

  const { body = [], disclaimer, header = [] } = serializedData ?? {};

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(error ? goBack : goHome);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerBackVisible: true,
          headerShown: true,
          gestureEnabled: !error,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <NotEligibleHeader header={header} />
      <NotEligibleContent body={body} />
      {disclaimer ? (
        <BodyXSText style={styles.disclaimer}>{disclaimer}</BodyXSText>
      ) : null}

      <ScreenTransitionOverlay
        isLoading={isFetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-notEligibleScreen"
              trackingAnalyticsId="not-eligible-error-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_QUALIFICATION_DETAILS,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingHorizontal: theme.spacing.m,
  },
  defaultStyle: {
    marginTop: theme.spacing.m,
  },
  disclaimer: {
    color: theme.color.text.secondary,
    marginTop: theme.spacing.m,
  },
  title: {
    marginTop: theme.spacing.m,
  },
});

export default NotEligibleScreen;
